import React, { useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import HRLayout from "../HRLayout";
import useHRCopy from "../i18n/useHRCopy";
import Label from "../components/Label";
import HRButton from "../components/HRButton";
import Reveal from "../components/Reveal";
import CapabilityRow from "../components/CapabilityRow";
import DoorCard from "../components/DoorCard";
import DOORS from "../components/doorImages";

const EASE = [0.22, 1, 0.36, 1];

// Splits the hero title on the keyword and wraps it in the royal underline.
function HeroTitle({ title, keyword }) {
  const idx = keyword ? title.indexOf(keyword) : -1;
  if (idx === -1) return <>{title}</>;
  return (
    <>
      {title.slice(0, idx)}
      <span className="hr-underline">{keyword}</span>
      {title.slice(idx + keyword.length)}
    </>
  );
}

export default function AnaMenu() {
  const copy = useHRCopy();
  const reduced = useReducedMotion();
  const videoRef = useRef(null);

  useEffect(() => {
    const prev = document.title;
    document.title = `${copy.brand.name} — ${copy.brand.tagline}`;
    return () => {
      document.title = prev;
    };
  }, [copy]);

  useEffect(() => {
    if (reduced) videoRef.current?.pause();
  }, [reduced]);

  const rise = (delay) =>
    reduced
      ? {}
      : {
          initial: { opacity: 0, y: 12 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.7, delay, ease: EASE },
        };

  return (
    <HRLayout navVariant="video">
      {/* 1 — Full-screen video hero */}
      <section className="relative h-screen min-h-[640px] overflow-hidden">
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/images/hero-poster.jpg"
          src="/videos/bg.mp4"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(17,28,48,0.55), rgba(17,28,48,0) 280px), linear-gradient(135deg, rgba(17,28,48,0.55), rgba(17,28,48,0.28))",
          }}
        />
        <div className="relative z-10 h-full flex items-center justify-center px-6">
          <div className="max-w-[900px] text-center">
            <motion.h1
              {...rise(0.08)}
              className="font-hanken font-extrabold text-white tracking-[-0.03em] leading-[1.05] text-[clamp(2.5rem,5vw,4.4rem)]"
            >
              <HeroTitle title={copy.anamenu.hero.title} keyword={copy.anamenu.hero.keyword} />
            </motion.h1>
            <motion.p
              {...rise(0.22)}
              className="mt-6 text-white/85 text-lg leading-relaxed max-w-xl mx-auto"
            >
              {copy.anamenu.hero.body}
            </motion.p>
            <motion.div
              {...rise(0.36)}
              className="mt-9 flex flex-wrap items-center justify-center gap-4"
            >
              <HRButton to="/iletisim" variant="primary" arrow>
                {copy.anamenu.hero.primaryCta}
              </HRButton>
              <HRButton to="/hizmetlerim" variant="ghost">
                {copy.anamenu.hero.secondaryCta}
              </HRButton>
            </motion.div>
          </div>
        </div>
        {!reduced && (
          <div
            aria-hidden="true"
            className="absolute left-1/2 -translate-x-1/2 bottom-7 z-10 w-px h-10 overflow-hidden"
          >
            <motion.span
              className="block w-px h-full bg-gradient-to-b from-transparent to-white/90"
              initial={{ y: "-100%" }}
              animate={{ y: "100%" }}
              transition={{ duration: 2.2, ease: EASE, repeat: Infinity }}
            />
          </div>
        )}
      </section>

      {/* 2 — Ne Yapıyoruz */}
      <section className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <Reveal className="lg:col-span-4 lg:sticky lg:top-28 self-start">
            <Label>{copy.anamenu.capabilities.eyebrow}</Label>
            <h2 className="mt-5 font-hanken font-extrabold text-ink tracking-[-0.02em] leading-[1.1] text-[clamp(1.9rem,3.4vw,2.8rem)]">
              {copy.anamenu.capabilities.title}
            </h2>
          </Reveal>
          <div className="lg:col-span-8 border-t border-line">
            {copy.anamenu.capabilities.items.map((it, i) => (
              <Reveal key={it.title} delay={i * 0.09}>
                <CapabilityRow title={it.title} body={it.body} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 3 — Hizmetlerimiz: photo doors */}
      <section className="bg-platinum border-y border-line">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <Label>{copy.anamenu.services.eyebrow}</Label>
            <h2 className="mt-5 font-hanken font-extrabold text-ink tracking-[-0.02em] leading-[1.1] text-[clamp(1.9rem,3.4vw,2.8rem)] max-w-2xl">
              {copy.anamenu.services.title}
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 gap-6 lg:gap-8">
            {copy.anamenu.services.cards.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.09}>
                <DoorCard
                  img={DOORS[i].img}
                  imgPosition={DOORS[i].pos}
                  alt={c.title}
                  title={c.title}
                  body={c.body}
                  href={c.href}
                  cta={c.cta}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — CTA band */}
      <section className="bg-navy">
        <Reveal className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-24 flex flex-wrap items-center justify-between gap-9">
          <div>
            <h2 className="font-hanken font-extrabold text-white tracking-[-0.02em] text-[clamp(1.9rem,3.4vw,2.7rem)]">
              {copy.anamenu.cta.title}
            </h2>
            <p className="mt-3 text-white/75 text-lg max-w-xl">{copy.anamenu.cta.body}</p>
          </div>
          <div className="flex flex-col items-start gap-3">
            <HRButton to="/iletisim" variant="paper" arrow>
              {copy.anamenu.cta.button}
            </HRButton>
            <a
              href={`mailto:${copy.anamenu.cta.email}`}
              className="text-white/70 hover:text-white text-[14.5px] font-medium transition-colors duration-300"
            >
              {copy.anamenu.cta.email}
            </a>
          </div>
        </Reveal>
      </section>
    </HRLayout>
  );
}
